import React, { useContext, useEffect, useState } from 'react'
import '../index.scss'
import { StoresContext } from '@App/contexts'
import RootStore from '@App/stores/root-store'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
    rightsholderMaintenanceFormFields,
    RightsholderMaintenanceInput,
    rightsholderMaintenanceSchema,
    RightsholderMaintenanceType,
} from '@Entities/schemas/rightsholder/rightsholder-maintenance-schema'
import { rightsholderModelToZodMapper, zodToRightsholderModelMapper } from '@App/helpers/rightsholder-model-zod-mappers'
import TopSection from '@Scenes/RightsholderScene/RightsholderMaintenance/RightsholderMaintenanceContactTab/RightsholderMaintenanceContactEdit/TopSection'
import IdentifiersSection from '@Scenes/RightsholderScene/RightsholderMaintenance/RightsholderMaintenanceContactTab/RightsholderMaintenanceContactEdit/IdentifiersSection'
import { observer } from 'mobx-react'
import convertEmptyValuesToUndefined from '@App/forms/v2/helpers/convert-empty-values-to-undefined'

interface RightsholderMaintenanceContactEditProps {
    pending: boolean
    setEditable: (editable: boolean) => void
    checkPending(): void
}

const RightsholderMaintenanceContactEdit = (props: RightsholderMaintenanceContactEditProps) => {
    const { pending, setEditable, checkPending } = props
    const { preloaderStore, popupStore, rightsholderStore, userStore } = useContext<RootStore>(StoresContext)

    const [rightsHolderData, setRightsHolderData] = useState<Partial<RightsholderMaintenanceType>>({})

    const methods = useForm<RightsholderMaintenanceInput, unknown, RightsholderMaintenanceType>({
        mode: 'onChange',
        criteriaMode: 'all',
        resolver: zodResolver(rightsholderMaintenanceSchema),
    })

    useEffect(() => {
        setupRightsHolderData()
        return () => {
            setEditable(false)
        }
    }, [])

    const setupRightsHolderData = () => {
        const localMemberSummaryInfo = rightsholderStore.memberSummary
        const parsedData = rightsholderModelToZodMapper(localMemberSummaryInfo)
        setRightsHolderData(parsedData)
    }

    useEffect(() => {
        initializeData()
    }, [rightsHolderData])

    const initializeData = () => {
        // Sets up default values for form now that we have rightsholder data
        methods.reset({ ...rightsHolderData })
    }

    const onSubmit = async (data: RightsholderMaintenanceType) => {
        if (data) {
            try {
                preloaderStore.openTransparentPreloader(true)
                const updateData = zodToRightsholderModelMapper(data)
                await rightsholderStore.editRightsholder(
                    rightsholderStore.currentRightsholderId,
                    convertEmptyValuesToUndefined(updateData)
                )
                checkPending()
                setEditable(false)
            } catch (error) {
                popupStore.setAlertFromError(error)
            }
            preloaderStore.openTransparentPreloader(false)
        }
    }

    const { name, hfaPublisherNumber, ipiNumber, isni } = rightsholderMaintenanceFormFields

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
                <div className="rightsholder-maintenance-contact-tab">
                    <TopSection
                        disabled={pending || userStore.isInternal || !methods.formState.isDirty}
                        setNonEditable={() => setEditable(false)}
                        name={name}
                    />
                    <IdentifiersSection
                        hfaPublisherNumber={hfaPublisherNumber}
                        isni={isni}
                        ipiNumber={ipiNumber}
                        rightsHolderData={rightsHolderData}
                    />
                </div>
            </form>
        </FormProvider>
    )
}
export default observer(RightsholderMaintenanceContactEdit)
